import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useCallback
} from "react";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AssignmentIcon from "@mui/icons-material/Assignment";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../../components/ConfirmationModal";
import ImportantClientNoteModal from "../../components/ImportantClientNoteModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const STATUS_LABELS = {
  pending: "Pendente",
  in_progress: "Em andamento",
  waiting_customer: "Aguardando cliente",
  done: "Concluído",
  cancelled: "Cancelado"
};
const STATUS_COLORS = {
  pending: "warning",
  in_progress: "info",
  waiting_customer: "default",
  done: "success",
  cancelled: "default"
};
const PRIORITY_LABELS = {
  low: "Baixa",
  medium: "Média",
  high: "Alta",
  urgent: "Urgente"
};
const PRIORITY_COLORS = {
  low: "default",
  medium: "info",
  high: "warning",
  urgent: "error"
};
const CATEGORY_LABELS = {
  api: "API",
  plataforma: "Plataforma",
  app: "App",
  tag: "Tag",
  chip: "Chip",
  financeiro: "Financeiro",
  outro: "Outro"
};

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD":
      return [...state, ...action.payload];
    case "UPSERT": {
      const record = action.payload;
      const idx = state.findIndex((s) => s.id === record.id);
      if (idx !== -1) {
        const newState = [...state];
        newState[idx] = record;
        return newState;
      }
      return [record, ...state];
    }
    case "RESET":
      return [];
    default:
      return state;
  }
};

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleDateString("pt-BR");
  } catch {
    return "-";
  }
};

const isOpenStatus = (status) => status !== "done" && status !== "cancelled";

const isOverdue = (note) => {
  if (!note.dueAt || !isOpenStatus(note.status)) return false;
  const today = new Date(new Date().toDateString());
  return new Date(note.dueAt) < today;
};

// Cor de destaque (borda esquerda) por situação da anotação.
const rowAccent = (note) => {
  if (note.status === "cancelled") return "#9e9e9e";
  if (note.priority === "urgent" && isOpenStatus(note.status)) return "#d32f2f";
  if (isOverdue(note)) return "#d32f2f";
  if (note.status === "done") return "#2e7d32";
  if (note.status === "in_progress") return "#0288d1";
  if (note.status === "waiting_customer") return "#ed6c02";
  return "#fbc02d"; // pending
};

const QUICK_FILTERS = ["all", "pending", "urgent", "overdue", "done"];
const QUICK_FILTER_PREDICATE = {
  all: () => true,
  pending: (n) => isOpenStatus(n.status),
  urgent: (n) => n.priority === "urgent" && isOpenStatus(n.status),
  overdue: (n) => isOverdue(n),
  done: (n) => n.status === "done"
};

const ImportantClientNotes = () => {
  const { user, socket } = useContext(AuthContext);
  const location = useLocation();
  const contactIdFilter =
    new URLSearchParams(location.search).get("contactId") || undefined;

  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [quickFilter, setQuickFilter] = useState("all");

  const [notes, dispatch] = useReducer(reducer, []);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingNote, setDeletingNote] = useState(null);

  const fetchNotes = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/important-client-notes", {
        params: {
          searchParam,
          pageNumber,
          status: statusFilter || undefined,
          priority: priorityFilter || undefined,
          category: categoryFilter || undefined,
          contactId: contactIdFilter
        }
      });
      dispatch({ type: "LOAD", payload: data.records });
      setHasMore(data.hasMore);
      if (typeof data.count === "number") setTotalCount(data.count);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [searchParam, pageNumber, statusFilter, priorityFilter, categoryFilter, contactIdFilter]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, statusFilter, priorityFilter, categoryFilter, contactIdFilter]);

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchNotes();
    }, 300);
    return () => clearTimeout(delay);
  }, [fetchNotes]);

  useEffect(() => {
    if (!socket) return undefined;
    const onEvent = (data) => {
      if (data.action === "create" || data.action === "update") {
        dispatch({ type: "UPSERT", payload: data.record });
      }
    };
    const channel = `company-${user.companyId}-importantclientnote`;
    socket.on(channel, onEvent);
    return () => socket.off(channel, onEvent);
  }, [socket, user.companyId]);

  const handleOpenModal = () => {
    setSelectedNote(null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedNote(null);
    setModalOpen(false);
  };

  const handleReload = () => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    fetchNotes();
  };

  const handleEdit = (note) => {
    setSelectedNote(note);
    setModalOpen(true);
  };

  const handleDone = async (note) => {
    try {
      await api.patch(`/important-client-notes/${note.id}/done`);
      toast.success(i18n.t("importantClientNotes.toasts.done"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async (note) => {
    try {
      await api.delete(`/important-client-notes/${note.id}`);
      toast.success(i18n.t("importantClientNotes.toasts.cancelled"));
    } catch (err) {
      toastError(err);
    }
    setDeletingNote(null);
    setConfirmOpen(false);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setPageNumber((prev) => prev + 1);
    }
  };

  const handleLoadMore = () => {
    if (hasMore && !loading) setPageNumber((prev) => prev + 1);
  };

  const clientLabel = (note) =>
    note.customerName || note.contact?.name || "-";

  // Exporta os registros atualmente visíveis (busca + filtros + filtro rápido).
  const handleExportCsv = () => {
    const sep = ";";
    const esc = (val) => {
      const s = val === null || val === undefined ? "" : String(val);
      // Sempre entre aspas; aspas internas duplicadas. Cobre ; aspas e \n.
      return `"${s.replace(/"/g, '""')}"`;
    };
    const dateOnly = (v) => (v ? new Date(v).toLocaleDateString("pt-BR") : "");
    const dateTime = (v) => (v ? new Date(v).toLocaleString("pt-BR") : "");

    const headers = [
      "Data da solicitação",
      "Cliente",
      "Título",
      "Descrição",
      "Categoria",
      "Prioridade",
      "Status",
      "Responsável",
      "Prazo",
      "Data de conclusão",
      "Criado em",
      "Atualizado em"
    ];

    const rows = visibleNotes.map((n) => [
      dateOnly(n.requestedAt),
      clientLabel(n),
      n.title || "",
      n.description || "",
      CATEGORY_LABELS[n.category] || n.category || "",
      PRIORITY_LABELS[n.priority] || n.priority || "",
      STATUS_LABELS[n.status] || n.status || "",
      n.assignedUser?.name || "",
      dateOnly(n.dueAt),
      dateTime(n.completedAt),
      dateTime(n.createdAt),
      dateTime(n.updatedAt)
    ]);

    const csv = [headers, ...rows]
      .map((cols) => cols.map(esc).join(sep))
      .join("\r\n");

    // BOM para o Excel reconhecer UTF-8.
    const blob = new Blob(["﻿" + csv], {
      type: "text/csv;charset=utf-8;"
    });
    const today = new Date().toISOString().slice(0, 10);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `anotacoes-importantes-${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const hasActiveFilters =
    !!searchParam ||
    !!statusFilter ||
    !!priorityFilter ||
    !!categoryFilter ||
    quickFilter !== "all" ||
    !!contactIdFilter;

  // Filtro rápido aplicado sobre os registros já carregados.
  const visibleNotes = notes.filter(
    QUICK_FILTER_PREDICATE[quickFilter] || QUICK_FILTER_PREDICATE.all
  );

  // Resumo (a partir da lista carregada — sem backend novo).
  const summary = {
    open: notes.filter((n) => isOpenStatus(n.status)).length,
    urgent: notes.filter(
      (n) => n.priority === "urgent" && isOpenStatus(n.status)
    ).length,
    overdue: notes.filter((n) => isOverdue(n)).length,
    done: notes.filter((n) => n.status === "done").length
  };

  const quickFilterLabel = (key) =>
    i18n.t(`importantClientNotes.quick.${key}`);

  const SummaryCard = ({ label, value, color }) => (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        minWidth: 110,
        p: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        borderLeft: `4px solid ${color}`,
        bgcolor: "#fff"
      }}
    >
      <Typography variant="h5" fontWeight={700} sx={{ color }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{ minHeight: "100vh", py: { xs: 3, md: 4 }, px: { xs: 2, md: 3 }, bgcolor: "#f8fafc" }}>
      <Container maxWidth="xl">
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <AssignmentIcon color="primary" sx={{ fontSize: 36 }} />
            <Box>
              <Typography variant="h5" fontWeight={700}>
                {i18n.t("importantClientNotes.title")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {i18n.t("importantClientNotes.subtitle")}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCsv}
              disabled={visibleNotes.length === 0}
            >
              {i18n.t("importantClientNotes.buttons.exportCsv")}
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenModal}>
              {i18n.t("importantClientNotes.buttons.add")}
            </Button>
          </Stack>
        </Stack>

        {/* Cards de resumo (a partir dos registros carregados) */}
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <SummaryCard label={i18n.t("importantClientNotes.summary.open")} value={summary.open} color="#fbc02d" />
          <SummaryCard label={i18n.t("importantClientNotes.summary.urgent")} value={summary.urgent} color="#d32f2f" />
          <SummaryCard label={i18n.t("importantClientNotes.summary.overdue")} value={summary.overdue} color="#ef6c00" />
          <SummaryCard label={i18n.t("importantClientNotes.summary.done")} value={summary.done} color="#2e7d32" />
        </Stack>

        {/* Filtros rápidos */}
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          {QUICK_FILTERS.map((key) => (
            <Chip
              key={key}
              label={quickFilterLabel(key)}
              clickable
              color={quickFilter === key ? "primary" : "default"}
              variant={quickFilter === key ? "filled" : "outlined"}
              onClick={() => setQuickFilter(key)}
            />
          ))}
        </Stack>

        {/* Filtros */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 2 }}
          flexWrap="wrap"
          useFlexGap
        >
          <TextField
            placeholder={i18n.t("importantClientNotes.searchPlaceholder")}
            type="search"
            size="small"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20 }} />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 220, bgcolor: "#fff" }}
          />
          <TextField
            select size="small" label={i18n.t("importantClientNotes.filters.status")}
            value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 170, bgcolor: "#fff" }}
          >
            <MenuItem value="">{i18n.t("importantClientNotes.filters.all")}</MenuItem>
            {Object.keys(STATUS_LABELS).map((k) => (
              <MenuItem key={k} value={k}>{STATUS_LABELS[k]}</MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" label={i18n.t("importantClientNotes.filters.priority")}
            value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}
            sx={{ minWidth: 150, bgcolor: "#fff" }}
          >
            <MenuItem value="">{i18n.t("importantClientNotes.filters.all")}</MenuItem>
            {Object.keys(PRIORITY_LABELS).map((k) => (
              <MenuItem key={k} value={k}>{PRIORITY_LABELS[k]}</MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" label={i18n.t("importantClientNotes.filters.category")}
            value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
            sx={{ minWidth: 150, bgcolor: "#fff" }}
          >
            <MenuItem value="">{i18n.t("importantClientNotes.filters.all")}</MenuItem>
            {Object.keys(CATEGORY_LABELS).map((k) => (
              <MenuItem key={k} value={k}>{CATEGORY_LABELS[k]}</MenuItem>
            ))}
          </TextField>
        </Stack>

        <Paper elevation={0} onScroll={handleScroll} sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
          <TableContainer sx={{ maxHeight: "calc(100vh - 320px)" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("importantClientNotes.table.requestedAt")}</TableCell>
                  <TableCell>{i18n.t("importantClientNotes.table.client")}</TableCell>
                  <TableCell>{i18n.t("importantClientNotes.table.title")}</TableCell>
                  <TableCell>{i18n.t("importantClientNotes.table.category")}</TableCell>
                  <TableCell>{i18n.t("importantClientNotes.table.priority")}</TableCell>
                  <TableCell>{i18n.t("importantClientNotes.table.status")}</TableCell>
                  <TableCell>{i18n.t("importantClientNotes.table.assigned")}</TableCell>
                  <TableCell>{i18n.t("importantClientNotes.table.dueAt")}</TableCell>
                  <TableCell align="center">{i18n.t("importantClientNotes.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleNotes.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary" fontWeight={600}>
                        {hasActiveFilters
                          ? i18n.t("importantClientNotes.emptyFiltered")
                          : i18n.t("importantClientNotes.empty")}
                      </Typography>
                      {!hasActiveFilters && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {i18n.t("importantClientNotes.emptyHint")}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleNotes.map((note) => {
                    const accent = rowAccent(note);
                    const danger =
                      (note.priority === "urgent" && isOpenStatus(note.status)) ||
                      isOverdue(note);
                    return (
                      <TableRow
                        key={note.id}
                        sx={{
                          bgcolor: danger ? "rgba(239,68,68,0.06)" : "inherit",
                          opacity: note.status === "cancelled" ? 0.6 : 1,
                          borderLeft: `4px solid ${accent}`
                        }}
                      >
                        <TableCell>{formatDate(note.requestedAt)}</TableCell>
                        <TableCell>{clientLabel(note)}</TableCell>
                        <TableCell>
                          <Tooltip title={note.description || ""}>
                            <span>{note.title}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{CATEGORY_LABELS[note.category] || note.category}</TableCell>
                        <TableCell>
                          <Chip size="small" color={PRIORITY_COLORS[note.priority] || "default"} label={PRIORITY_LABELS[note.priority] || note.priority} />
                        </TableCell>
                        <TableCell>
                          <Chip size="small" color={STATUS_COLORS[note.status] || "default"} label={STATUS_LABELS[note.status] || note.status} />
                        </TableCell>
                        <TableCell>{note.assignedUser?.name || "-"}</TableCell>
                        <TableCell>
                          {formatDate(note.dueAt)}
                          {isOverdue(note) && (
                            <Chip
                              size="small"
                              label={i18n.t("importantClientNotes.overdueTag")}
                              sx={{ ml: 1, bgcolor: "#d32f2f", color: "#fff", height: 18 }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title={i18n.t("importantClientNotes.buttons.done")}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="success"
                                  disabled={note.status === "done" || note.status === "cancelled"}
                                  onClick={() => handleDone(note)}
                                >
                                  <CheckCircleOutlineIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={i18n.t("importantClientNotes.buttons.edit")}>
                              <IconButton size="small" onClick={() => handleEdit(note)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={i18n.t("importantClientNotes.buttons.delete")}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={note.status === "cancelled"}
                                  onClick={() => {
                                    setDeletingNote(note);
                                    setConfirmOpen(true);
                                  }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}
          >
            <Typography variant="caption" color="text.secondary">
              {i18n.t("importantClientNotes.showing")}{" "}
              {visibleNotes.length} {i18n.t("importantClientNotes.of")}{" "}
              {Math.max(totalCount, notes.length)}
            </Typography>
            {hasMore && (
              <Button size="small" onClick={handleLoadMore} disabled={loading}>
                {i18n.t("importantClientNotes.loadMore")}
              </Button>
            )}
          </Stack>
        </Paper>
      </Container>

      <ImportantClientNoteModal
        open={modalOpen}
        onClose={handleCloseModal}
        noteId={selectedNote && selectedNote.id}
        reload={handleReload}
      />

      <ConfirmationModal
        title={i18n.t("importantClientNotes.confirmationModal.deleteTitle")}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => handleDelete(deletingNote)}
      >
        {i18n.t("importantClientNotes.confirmationModal.deleteMessage")}
      </ConfirmationModal>
    </Box>
  );
};

export default ImportantClientNotes;
